import { API_GQ, error } from '../helpers/apiConfig';
import { CommonHistoryResponseObj, STKFiltersVoucherInput  } from '../models/history';
import { createContext, ReactNode, useContext, useState } from 'react';
import { Field } from '../models/home';
import { formatDateInYYYYMMDD } from  '../helpers/date';
import { GET_HISTORY_IFS_USERS, GET_HISTORY_NON_IFS_USERS, GET_HISTORY_VOUCHER_CODES } from '../queries/common';
import { MUTATE_RESEND_COMPENSATION_CODES, MUTATE_UPDATE_EMAIL_ADDRESS } from '../mutations/common';
import { ResendCompensationPayload, UpdateEmailPayload } from '../models/common';
import { useApiError } from './ApiErrorProvider';
import { useAuthProvider } from './AuthProvider';
import { v4 as uuidv4 } from 'uuid';

interface Props {
  children: ReactNode;
}

const HistoryContext = createContext({
  emailSent: 0,
  emailUpdated: false,
  historyCompensationCodes: [] as string[],
  historyIfs: [] as CommonHistoryResponseObj[],
  historyNonIfs: [] as CommonHistoryResponseObj[],
  historyNonIfsPageState: {
    requestNextPageToken: [''],
    errorNextPageToken: [''],
    number: 0,
    size: 10
  },
  loading: {
    history: false,
    resend: false,
    voucherCode: false,
    updateEmail: false
  },
  getIFSHistory: () => {},
  getNonIFSHistory: (lastName: string, limit: number, page: number, pnrLocator: string, scheduledAt: string, ticketNumber: string) => {},
  resendCompensation: (payload: ResendCompensationPayload) => {},
  setEmailSent: (flag: number) => {},
  setEmailUpdated: (flag: boolean) => {},
  updateEmailAddress: (payload: UpdateEmailPayload, searchFields: Field[], searchNonIFS?: boolean) => {},
  viewCompensationCodes: (payload: string[]) => {}
});

export const HistoryProvider = ({ children }: Props) => {
  const authContext = useAuthProvider();
  const { setErrorCode } = useApiError();
  
  const [emailSent, setEmailSent] = useState(0);
  const [emailUpdated, setEmailUpdated] = useState(false);
  const [historyCompensationCodes, setHistoryCompensationCodes] = useState<string[]>([]);
  const [historyIfs, setHistoryIfs] = useState<CommonHistoryResponseObj[]>([]);
  const [historyNonIfs, setHistoryNonIfs] = useState<CommonHistoryResponseObj[]>([]);
  const [historyNonIfsPageState, setHistoryNonIfsPageState] = useState({
    requestNextPageToken: [''],
    errorNextPageToken: [''],
    number: 0,
    size: 10
  })
  const [loading, setLoading] = useState({
    history: false,
    resend: false,
    voucherCode: false,
    updateEmail: false
  });
  const [historyNonIfsRequestId, setHistoryNonIfsRequestId] = useState<string | undefined>(undefined);

  const getIFSHistory = async () => {
    try {
      setLoading(prevState => {
        return {
          ...prevState,
          history: true
        }
      });
      const body = { 
        query: GET_HISTORY_IFS_USERS,
        variables: {}
      };
      const accessToken = await authContext.acquireTokenSilently();
      const response = await API_GQ.post(accessToken,body);
      const data = await response.json();        
      const historyIfs: CommonHistoryResponseObj[] = data.data.stkGetUserHistory.slice().map(item => 
        {
          const uniqueId = uuidv4();
          return (
            {
              id: uniqueId, 
              ...item
            }
          );
        }          
      );

      setHistoryIfs(historyIfs);
      setLoading(prevState => {
        return {
          ...prevState,
          history: false
        }
      });
    } catch (e: any) {
      setLoading(prevState => {
        return {
          ...prevState,
          history: false
        }
      });
        setErrorCode(error.DEFAULT);
    }
  };

  const getNonIFSHistory = async (lastName: string, limit: number, page: number, pnrLocator: string, scheduledAt: string, ticketNumber: string) => {
    try {
      setLoading(prevState => {
        return {
          ...prevState,
          history: true
        }
      });
      let payload: STKFiltersVoucherInput  = {
        limit: limit,
        page: page+1
      }
      if (lastName) payload.lastName = lastName;
      if (page > 0 && historyNonIfsPageState.requestNextPageToken[page]) {
        payload.requestNextPageToken = historyNonIfsPageState.requestNextPageToken[page];
      }
      if (page > 0 && historyNonIfsPageState.errorNextPageToken[page]) {
        payload.errorNextPageToken = historyNonIfsPageState.errorNextPageToken[page];
      }
      if (pnrLocator || ticketNumber) {
        payload.pnrLocator = pnrLocator ? pnrLocator : ticketNumber;
      }
      if (scheduledAt) payload.scheduledAt = formatDateInYYYYMMDD(scheduledAt);

      const body = { 
        query: GET_HISTORY_NON_IFS_USERS,
        variables: {
          payload: { ...payload }
        }
      };
      const accessToken = await authContext.acquireTokenSilently();
      const response = await API_GQ.post(accessToken,body);
      const data = await response.json();

      let vouchers = [] as CommonHistoryResponseObj[];
      if (data.data?.stkGetFilterVoucher?.releasedVouchers) {
        vouchers = [...data.data.stkGetFilterVoucher.releasedVouchers];
      }
      if (data.data?.stkGetFilterVoucher?.erroredVouchers) {
        if(vouchers.length) {
          vouchers = vouchers.concat(data.data.stkGetFilterVoucher.erroredVouchers);
        } else {
          vouchers = [...data.data.stkGetFilterVoucher.erroredVouchers];
        }
      }
      if (data.data?.stkGetFilterVoucher?.compensationRequests) {
        if(vouchers.length) {
          vouchers = vouchers.concat(data.data.stkGetFilterVoucher.compensationRequests);
        } else {
          vouchers = [...data.data.stkGetFilterVoucher.compensationRequests];
        }
      }
      setHistoryNonIfs(vouchers);
      setHistoryNonIfsPageState({
        requestNextPageToken:
          limit !== historyNonIfsPageState.size || page === 0
            ? [''].concat(data.data?.stkGetFilterVoucher?.requestNextPageToken ?? '') 
            : page < historyNonIfsPageState.number 
              ? historyNonIfsPageState.requestNextPageToken
              : historyNonIfsPageState.requestNextPageToken.concat(data.data?.stkGetFilterVoucher?.requestNextPageToken ?? ''),
        errorNextPageToken:
          limit !== historyNonIfsPageState.size || page === 0
            ? [''].concat(data.data?.stkGetFilterVoucher?.errorNextPageToken ?? '') 
            : page < historyNonIfsPageState.number 
              ? historyNonIfsPageState.errorNextPageToken
              : historyNonIfsPageState.errorNextPageToken.concat(data.data?.stkGetFilterVoucher?.errorNextPageToken ?? ''),
        number: page,
        size: limit
      });
      setHistoryNonIfsRequestId(data.data?.stkGetFilterVoucher?.requestId);
      setLoading(prevState => {
        return {
          ...prevState,
          history: false
        }
      });
    } catch (e: any) {
        setLoading(prevState => {
          return {
            ...prevState,
            history: false
          }
        });
        setErrorCode(error.DEFAULT);
    }
  };
  
  const resendCompensation = async (payload: ResendCompensationPayload) => {
    const body = {
      query: MUTATE_RESEND_COMPENSATION_CODES,
      variables: {
        payload: payload
      }
    };

    try {
      setLoading(prevState => {
        return {
          ...prevState,
          resend: true
        }
      });
      const accessToken = await authContext.acquireTokenSilently();
      const response = await API_GQ.post(accessToken, body);
      const data = await response.json();

      if (data) {
        if (data.errors) {
          setErrorCode(error.DEFAULT);
        } else {
          if (data.data.resendEmail && data.data.resendEmail.success) {
            setEmailSent(prev => prev+1);
          } else {
            setEmailSent(0);
            setErrorCode(error.DEFAULT);
          }
        }
      }
      setLoading(prevState => {
        return {
          ...prevState,
          resend: false
        }
      });
    } catch (e: any) {
      setLoading(prevState => {
        return {
          ...prevState,
          resend: false
        }
      });
      setErrorCode(error.DEFAULT);
    }
  };

  const viewCompensationCodes = async (payload: string[]) => {
    const body = {
      query: GET_HISTORY_VOUCHER_CODES,
      variables: {
        voucherIds: payload
      }
    };

    try {
      setLoading(prevState => {
        return {
          ...prevState,
          voucherCode: true
        }
      });
      const accessToken = await authContext.acquireTokenSilently();
      const response = await API_GQ.post(accessToken, body);
      const data = await response.json();
      console.log("d", data)
      if (data) {
        if (data.errors) {
          setErrorCode(error.DEFAULT);
        } else {
          if (data.data.getReleasedVoucherCode.every((item: any) => item.responseStatusCode === 404)) {
            setErrorCode(error.DEFAULT);
          } else {
            const validVoucherCodes: string[] = data.data.getReleasedVoucherCode.filter((item: any) => item.responseStatusCode === 200).map((item: any) => item.voucherCode);
            setHistoryCompensationCodes(validVoucherCodes);
          }          
        }
      }
      setLoading(prevState => {
        return {
          ...prevState,
          voucherCode: false
        }
      });
    } catch (e: any) {
      setLoading(prevState => {
        return {
          ...prevState,
          voucherCode: false
        }
      });
      setErrorCode(error.DEFAULT);
    }
  };

  const updateEmailAddress = async (payload: UpdateEmailPayload, searchFields: Field[], searchNonIFS: boolean = true) => {
    try {
        setLoading(prevState => {
            return {
                ...prevState,
                updateEmail: true
            }
        });
        // Construct the mutation payload as per availability of requestId or voucherId
        const mutationPayload: any = {
            passengerEmailAddress: payload.passengerEmailAddress,
            voucherType: payload.voucherType || 'WIFI',
        };
        if (payload.requestId) {
            mutationPayload.requestId = payload.requestId;
        }
        if (!payload.requestId && payload.voucherId) {
            mutationPayload.voucherId = payload.voucherId;
        }
        const body = { 
            query: MUTATE_UPDATE_EMAIL_ADDRESS,
            variables: {
                payload: mutationPayload
            }
        };
        const accessToken = await authContext.acquireTokenSilently();
        const response = await API_GQ.post(accessToken,body);
        const data = await response.json();

        if (data.errors) {
            setEmailUpdated(false);
            setErrorCode(error.DEFAULT);
            setLoading(prevState => {
                return {
                    ...prevState,
                    updateEmail: false
                }
            });
        } else {
            // Use the new mutation response structure
            const result = data.data.stkHistoryEmail;
            if (result && result.statusCode === 200) {
                setTimeout(async () => {
                    try {
                        await getIFSHistory();

                        // Update non-IFS history if on non-IFS page
                        if(searchNonIFS) {
                            const lastName = searchFields[0].value;
                            const date = searchFields[1]?.value?.length ? formatDateInYYYYMMDD(searchFields[1].value) : searchFields[1].value;
                            const bookingReference = searchFields[2].value;
                            const ticketNumber = searchFields[3].value;
                            const page = historyNonIfsPageState.number;
                            const limit = historyNonIfsPageState.size;

                            // Fetch updated non-IFS history
                            await getNonIFSHistory(lastName, limit, page, bookingReference, date, ticketNumber);
                        }
                    } finally {
                        setLoading(prevState => ({
                            ...prevState,
                            history: false
                        }));
                    }

                    setEmailUpdated(true);
                    setLoading(prevState => {
                        return {
                            ...prevState,
                            updateEmail: false
                        }
                    });
                }, 5000); 
            } else {
                setEmailUpdated(false);
                setErrorCode(result?.statusMessage || result?.message || error.DEFAULT);
                setLoading(prevState => {
                    return {
                        ...prevState,
                        updateEmail: false
                    }
                });
            }
        }
    } catch (e: any) {
        setLoading(prevState => {
            return {
                ...prevState,
                updateEmail: false
            }
        });
        setErrorCode(error.DEFAULT);
    }
  };
  
  return (
    <HistoryContext.Provider 
      value={{
        emailSent,
        emailUpdated,       
        historyCompensationCodes,
        historyIfs,
        historyNonIfs,
        historyNonIfsPageState,
        loading,
        getIFSHistory,
        getNonIFSHistory,
        resendCompensation,
        setEmailSent,
        setEmailUpdated,
        updateEmailAddress,
        viewCompensationCodes
      }}
    >
      {children}
    </HistoryContext.Provider>
  );
};
  
export const useHistoryProvider = () => {
  return useContext(HistoryContext);
};